import React from 'react';
import {useUpdateIdentificationInfo} from '@rocket-ui/lk-api';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {Button} from 'ui';
import {Drawer} from 'shared/ui/Drawer';
import {getProfileEmailError, isProfileEmailValid} from '../../model/emailValidation';
import {getUpdateEmailErrorMessage} from '../../model/getUpdateEmailErrorMessage';
import styles from './EditEmailDrawer.module.css';

export type EditEmailDrawerProps = {
	open: boolean;
	initialEmail: string;
	onClose: () => void;
	onSaveSuccess?: (email: string) => void;
};

export function EditEmailDrawer({
	open,
	initialEmail,
	onClose,
	onSaveSuccess,
}: EditEmailDrawerProps): React.ReactElement {
	const isMaxSm = useIsMaxSm();
	const {mutateAsync: updateIdentificationInfo, isLoading} = useUpdateIdentificationInfo();
	const [value, setValue] = React.useState(initialEmail);
	const [showValidationError, setShowValidationError] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!open) {
			return;
		}

		setValue(initialEmail === '—' ? '' : initialEmail);
		setShowValidationError(false);
		setSubmitError(null);
	}, [open, initialEmail]);

	const validationError = showValidationError ? getProfileEmailError(value) : null;
	const error = submitError || validationError;
	const trimmed = value.trim();
	const canSave = trimmed.length > 0 && isProfileEmailValid(trimmed);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setValue(event.target.value);
		setShowValidationError(true);
		setSubmitError(null);
	};

	const handleClear = () => {
		setValue('');
		setShowValidationError(false);
		setSubmitError(null);
	};

	const handleClose = () => {
		if (isLoading) {
			return;
		}

		onClose();
	};

	const handleSave = async () => {
		setShowValidationError(true);
		setSubmitError(null);

		if (!canSave || isLoading) {
			return;
		}

		if (trimmed === (initialEmail === '—' ? '' : initialEmail).trim()) {
			onClose();
			return;
		}

		try {
			await updateIdentificationInfo({email: trimmed});
			onSaveSuccess?.(trimmed);
			onClose();
		} catch (error) {
			setSubmitError(getUpdateEmailErrorMessage(error));
		}
	};

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title="Введите новый e-mail"
			showCloseButton
			closeButtonPosition="end"
			variant={isMaxSm ? 'fullscreen' : 'side'}
			bodyClassName={styles.drawerBody}
			footerContent={
				<Button
					type="button"
					className="!w-full !h-[60px]"
					onClick={handleSave}
					disabled={!trimmed || isLoading}
					isLoading={isLoading}
				>
					Сохранить
				</Button>
			}
		>
			<div className={styles.field}>
				<TextInput
					name="email"
					type="email"
					className={styles.input}
					value={value}
					placeholder="name@example.com"
					autoComplete="email"
					inputMode="email"
					autoFocus
					disabled={isLoading}
					aria-invalid={Boolean(error)}
					onChange={handleChange}
					onClear={isLoading ? undefined : handleClear}
				/>
				{error ? <p className={styles.error}>{error}</p> : null}
			</div>
		</Drawer>
	);
}
